// Начините на плащане във формата на фактурата (`InvoiceCreateModal`).

import type { ActivePaymentMethod, PaymentMethod } from "../../../../../api/paymentMethods";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";

/** „По сметка" — единственият метод на свободната фактура (BE отказва другите). */
const ON_ACCOUNT_PAYMENT_METHOD_ID = 4;

/**
 * Свободна фактура — САМО „По сметка“ (банков път, без бон; BE
 * `free_invoice.py`, 0.0.389): от активните, иначе от мастър списъка
 * (деактивиран метод пак е името на фактурата).
 *
 * По сметки — САМО методите, с които сметките вече са платени: бонът им е
 * издаден, начинът на плащане на фактурата е неговият и не се сменя. Една
 * сметка (или всички с един метод) — една опция, изборът е заключен; сметки с
 * различни методи — само техните. Методът е от активните, иначе от мастър
 * списъка — „Смесено“ (сглобява се от „В брой“ и „С карта“ — свой ред за
 * устройството няма, затова `/payment-methods/active` не го връща) или вече
 * деактивиран метод, — иначе името от сметката. Без това изборът показваше
 * голото id („3“). Сметки без записан метод (стари) — активните, както преди.
 */
export function invoicePaymentOptionsOf(
  activeMethods: readonly ActivePaymentMethod[],
  allMethods: readonly PaymentMethod[],
  bills: readonly Pick<BillHistory, "payment_method_id" | "payment_method_name">[],
  free: boolean,
): ActivePaymentMethod[] {
  if (free) {
    const onAccount =
      activeMethods.find((method) => method.id === ON_ACCOUNT_PAYMENT_METHOD_ID) ??
      allMethods.find((method) => method.id === ON_ACCOUNT_PAYMENT_METHOD_ID);
    return [
      onAccount ?? {
        id: ON_ACCOUNT_PAYMENT_METHOD_ID,
        name_bg: "По сметка",
        name_en: "On account",
        sort_index: Number.MAX_SAFE_INTEGER,
      },
    ];
  }
  const known = new Set(activeMethods.map((method) => method.id));
  const fromBills: ActivePaymentMethod[] = [];
  for (const bill of bills) {
    const id = bill.payment_method_id;
    if (id === null || id === undefined || known.has(id)) continue;
    known.add(id);
    const master = allMethods.find((method) => method.id === id);
    const name = master?.name_bg ?? bill.payment_method_name;
    if (!name) continue;
    fromBills.push(master ?? { id, name_bg: name, name_en: name, sort_index: Number.MAX_SAFE_INTEGER });
  }
  const all = [...activeMethods, ...fromBills];
  const billMethodIds = new Set(
    bills
      .map((bill) => bill.payment_method_id)
      .filter((id): id is number => id !== null && id !== undefined),
  );
  const ofBills = all.filter((method) => billMethodIds.has(method.id));
  return ofBills.length > 0 ? ofBills : all;
}
